import { useEditor, EditorContent } from '@tiptap/react';
import { BubbleMenu } from '@tiptap/react/menus';
import { DragHandle } from '@tiptap/extension-drag-handle-react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Image from '@tiptap/extension-image';
import Highlight from '@tiptap/extension-highlight';
import Typography from '@tiptap/extension-typography';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import Mention from '@tiptap/extension-mention';
import { Details, DetailsSummary, DetailsContent } from '@tiptap/extension-details';
import { CodeBlockLowlight } from '@tiptap/extension-code-block-lowlight';
import { ReactNodeViewRenderer } from '@tiptap/react';
import { common, createLowlight } from 'lowlight';
import { Node, mergeAttributes, InputRule } from '@tiptap/core';
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Heading1, Heading2, Heading3, List, ListOrdered, CheckSquare, Quote, Minus,
  Type, Code, ImageIcon, Table as TableIcon, AlertCircle,
  ToggleLeft, Bold as BoldIcon, Italic as ItalicIcon, Underline as UnderlineIcon,
  Strikethrough, Link as LinkIcon, Code2, GripVertical, Plus,
  Sparkles, Video as VideoIcon, FileUp,
} from 'lucide-react';
import { VideoEmbed } from './editor/VideoNode';
import { FileAttachment } from './editor/FileNode';
import { AiBlock } from './editor/AiBlockNode';
import { CodeBlockNodeView } from './editor/CodeBlockNodeView.jsx';
import { TableToolbar } from './editor/TableToolbar.jsx';
import { uploadNoteAsset } from './editor/uploadHelper';

const lowlight = createLowlight(common);

// ─── D80 Tier 2: TWO distinct Mention extensions ───
// We split Mention into PersonMention (`@`) and PageMention (`+`) because:
//   1. Tiptap's single-node multi-`suggestions[]` config still installs
//      two ProseMirror plugins, but the parsed/render attrs are keyed
//      off `mentionSuggestionChar` — a brittle round-trip when storing
//      to Mongo and re-hydrating across surfaces.
//   2. Distinct node types let us style + parse + persist each kind
//      independently (different `data-*` attrs, different chip CSS).
// Each extension keeps its own attrs (id, label) and renders its own
// chip. Backend persistence treats both as opaque inline nodes.
const PersonMention = Mention.extend({
  name: 'personMention',
  addAttributes() {
    return {
      id: { default: null, parseHTML: (el) => el.getAttribute('data-id'), renderHTML: (a) => ({ 'data-id': a.id }) },
      label: { default: null, parseHTML: (el) => el.getAttribute('data-label'), renderHTML: (a) => ({ 'data-label': a.label }) },
    };
  },
  parseHTML() { return [{ tag: 'span[data-mention-type="person"]' }]; },
});

const PageMention = Mention.extend({
  name: 'pageMention',
  addAttributes() {
    return {
      id: { default: null, parseHTML: (el) => el.getAttribute('data-id'), renderHTML: (a) => ({ 'data-id': a.id }) },
      label: { default: null, parseHTML: (el) => el.getAttribute('data-label'), renderHTML: (a) => ({ 'data-label': a.label }) },
      kind: { default: 'note', parseHTML: (el) => el.getAttribute('data-kind') || 'note', renderHTML: (a) => ({ 'data-kind': a.kind || 'note' }) },
    };
  },
  parseHTML() { return [{ tag: 'span[data-mention-type="page"]' }]; },
});

// Code block extension with our React NodeView (language dropdown + copy).
const CodeBlock = CodeBlockLowlight.extend({
  addNodeView() { return ReactNodeViewRenderer(CodeBlockNodeView); },
}).configure({ lowlight, defaultLanguage: 'plaintext' });

// ─── D80: Callout node (block) — gold-tinted box, distinct from Quote ───
const Callout = Node.create({
  name: 'callout',
  group: 'block',
  content: 'block+',
  defining: true,
  parseHTML() {
    return [{ tag: 'div[data-callout="true"]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-callout': 'true', class: 'stellar-callout' }), 0];
  },
  addCommands() {
    return {
      setCallout: () => ({ commands }) => commands.wrapIn(this.name),
      toggleCallout: () => ({ commands }) => commands.toggleWrap(this.name),
      unsetCallout: () => ({ commands }) => commands.lift(this.name),
    };
  },
  addKeyboardShortcuts() {
    return {
      'Mod-Shift-c': () => this.editor.commands.toggleCallout(),
    };
  },
  addInputRules() {
    // Type `!!! ` at start of a block to convert it to a callout.
    return [
      new InputRule({
        find: /^!!!\s$/,
        handler: ({ state, range, commands }) => {
          commands.deleteRange(range);
          commands.toggleCallout();
        },
      }),
    ];
  },
});

// All slash commands first `clearNodes()` so the new block is created at
// the top level even if the cursor is currently inside a list / blockquote
// / callout / details. Without this, picking "Heading 1" from inside a
// bullet list would either no-op or nest the heading inside the list.
//
// Each command receives (editor, ctx) where ctx = { workspaceId,
// triggerUpload(kind) }. The Image/Video/File entries call
// `triggerUpload(...)` so the NotionEditor's hidden file <input> is
// clicked and the uploaded URL is wired into the corresponding Node.
const SLASH_COMMANDS = [
  { key: 'text', title: 'Text', icon: Type, desc: 'Plain paragraph', command: (e) => e.chain().focus().clearNodes().setParagraph().run() },
  { key: 'h1', title: 'Heading 1', icon: Heading1, desc: 'Large section heading', command: (e) => e.chain().focus().clearNodes().setHeading({ level: 1 }).run() },
  { key: 'h2', title: 'Heading 2', icon: Heading2, desc: 'Medium section heading', command: (e) => e.chain().focus().clearNodes().setHeading({ level: 2 }).run() },
  { key: 'h3', title: 'Heading 3', icon: Heading3, desc: 'Small section heading', command: (e) => e.chain().focus().clearNodes().setHeading({ level: 3 }).run() },
  { key: 'bullet', title: 'Bulleted list', icon: List, desc: 'Simple bulleted list', command: (e) => e.chain().focus().clearNodes().toggleBulletList().run() },
  { key: 'numbered', title: 'Numbered list', icon: ListOrdered, desc: 'List with numbers', command: (e) => e.chain().focus().clearNodes().toggleOrderedList().run() },
  { key: 'todo', title: 'To-do list', icon: CheckSquare, desc: 'Track tasks with checkboxes', command: (e) => e.chain().focus().clearNodes().toggleTaskList().run() },
  { key: 'toggle', title: 'Toggle', icon: ToggleLeft, desc: 'Collapsible block', command: (e) => {
    // Create the toggle OPEN so the cursor can dive into the
    // detailsContent area and the user can immediately add children.
    e.chain().focus().clearNodes().setDetails().run();
    // Force `open: true` on the just-created details node and place
    // the cursor inside its content area.
    const { state } = e;
    let pos = null;
    state.doc.nodesBetween(0, state.doc.content.size, (node, p) => {
      if (node.type.name === 'details') pos = p;
    });
    if (pos != null) {
      e.chain()
        .command(({ tr }) => {
          tr.setNodeAttribute(pos, 'open', true);
          return true;
        })
        .run();
    }
  } },
  { key: 'quote', title: 'Quote', icon: Quote, desc: 'Capture a quote', command: (e) => e.chain().focus().clearNodes().setBlockquote().run() },
  { key: 'callout', title: 'Callout', icon: AlertCircle, desc: 'Highlighted info block', command: (e) => e.chain().focus().clearNodes().setCallout().run() },
  { key: 'divider', title: 'Divider', icon: Minus, desc: 'Visual divider', command: (e) => e.chain().focus().clearNodes().setHorizontalRule().run() },
  { key: 'code', title: 'Code block', icon: Code, desc: 'Code snippet with language', command: (e) => e.chain().focus().clearNodes().toggleCodeBlock().run() },
  { key: 'table', title: 'Table', icon: TableIcon, desc: 'Add a table', command: (e) => e.chain().focus().clearNodes().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run() },
  // D80 Tier 2 — media + AI blocks.
  { key: 'image', title: 'Image', icon: ImageIcon, desc: 'Upload an image (≤25 MB)',
    command: (e, ctx) => ctx?.triggerUpload?.('image') },
  { key: 'video', title: 'Video', icon: VideoIcon, desc: 'Upload a video (≤100 MB)',
    command: (e, ctx) => ctx?.triggerUpload?.('video') },
  { key: 'file', title: 'File', icon: FileUp, desc: 'Upload any file (≤25 MB)',
    command: (e, ctx) => ctx?.triggerUpload?.('file') },
  { key: 'ai', title: 'AI block', icon: Sparkles, desc: 'In-note AI helper',
    command: (e) => e.chain().focus().clearNodes().insertAiBlock().run() },
];

function SlashMenu({ editor, isOpen, position, query, slashAnchor, onClose, slashContext }) {
  const [idx, setIdx] = useState(0);
  const filtered = SLASH_COMMANDS.filter(c => c.title.toLowerCase().includes((query || '').toLowerCase()));

  useEffect(() => { setIdx(0); }, [query]);

  // Apply a command — single source of truth (used by keyboard + click).
  const apply = useCallback((cmd) => {
    if (!cmd || !editor) return;
    // Delete the "/query" text using the anchor captured when the menu opened.
    if (typeof slashAnchor === 'number' && slashAnchor >= 0) {
      const docSize = editor.state.doc.content.size;
      const from = Math.min(slashAnchor, docSize);
      const to = Math.min(editor.state.selection.from, docSize);
      if (to > from) {
        editor.chain().focus().deleteRange({ from, to }).run();
      } else {
        editor.commands.focus();
      }
    } else {
      editor.commands.focus();
    }
    cmd.command(editor, slashContext);
    onClose();
  }, [editor, slashAnchor, onClose, slashContext]);

  useEffect(() => {
    if (!isOpen) return;
    const h = (e) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); setIdx(i => (i + 1) % filtered.length); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setIdx(i => (i - 1 + filtered.length) % filtered.length); }
      else if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); apply(filtered[idx]); }
      else if (e.key === 'Escape') { e.preventDefault(); onClose(); }
    };
    // Capture phase so we beat ProseMirror's Enter→newline handler.
    document.addEventListener('keydown', h, true);
    return () => document.removeEventListener('keydown', h, true);
  }, [isOpen, filtered, idx, apply, onClose]);

  if (!isOpen || filtered.length === 0) return null;

  return (
    <div
      className="fixed z-50 bg-popover border border-border rounded-xl shadow-2xl p-1 w-[300px] max-h-[360px] overflow-y-auto backdrop-blur-lg pointer-events-auto"
      style={{ top: position.top, left: position.left }}
      data-testid="slash-command-menu"
      onMouseDown={(e) => e.preventDefault()}
    >
      <p className="text-[10px] font-semibold text-muted-foreground/50 uppercase tracking-wider px-3 py-2">Blocks</p>
      {filtered.map((cmd, i) => (
        <button
          key={cmd.key}
          type="button"
          className={`flex items-center gap-3 w-full px-3 py-2 rounded-lg text-left transition-colors ${i === idx ? 'bg-secondary' : 'hover:bg-secondary/50'}`}
          onMouseEnter={() => setIdx(i)}
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => { e.preventDefault(); apply(cmd); }}
          data-testid={`slash-${cmd.title.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <div className="h-9 w-9 rounded-lg bg-background border border-border/50 flex items-center justify-center shrink-0">
            <cmd.icon className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="min-w-0">
            <p className="text-[13px] font-medium">{cmd.title}</p>
            <p className="text-[11px] text-muted-foreground/60">{cmd.desc}</p>
          </div>
        </button>
      ))}
    </div>
  );
}

// ─── D80: Inline toolbar (bubble menu) for B / I / U / S / code / link ───
const INLINE_BUBBLE_OPTIONS = { placement: 'top', offset: 8 };
const inlineShouldShow = ({ editor, state }) => {
  const { from, to, empty } = state.selection;
  if (empty) return false;
  if (editor.isActive('codeBlock')) return false;
  return to > from;
};
function InlineToolbar({ editor }) {
  if (!editor) return null;
  const btn = (active, onClick, testid, Icon, title) => (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`p-1.5 rounded-md transition-colors ${active ? 'bg-gold/20 text-gold' : 'hover:bg-foreground/[0.06] text-muted-foreground'}`}
      data-testid={testid}
      title={title}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
  return (
    <BubbleMenu
      editor={editor}
      options={INLINE_BUBBLE_OPTIONS}
      shouldShow={inlineShouldShow}
    >
      <div
        className="flex items-center gap-0.5 bg-popover border border-border rounded-lg shadow-2xl p-1 backdrop-blur-lg"
        data-testid="inline-toolbar"
      >
        {btn(editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), 'fmt-bold', BoldIcon, 'Bold (Cmd+B)')}
        {btn(editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), 'fmt-italic', ItalicIcon, 'Italic (Cmd+I)')}
        {btn(editor.isActive('underline'), () => editor.chain().focus().toggleUnderline().run(), 'fmt-underline', UnderlineIcon, 'Underline (Cmd+U)')}
        {btn(editor.isActive('strike'), () => editor.chain().focus().toggleStrike().run(), 'fmt-strike', Strikethrough, 'Strikethrough')}
        {btn(editor.isActive('code'), () => editor.chain().focus().toggleCode().run(), 'fmt-code', Code2, 'Inline code')}
        {btn(editor.isActive('link'), () => {
          const current = editor.getAttributes('link')?.href || '';
          const url = prompt('Link URL:', current);
          if (url === null) return;
          if (url === '') editor.chain().focus().unsetLink().run();
          else editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
        }, 'fmt-link', LinkIcon, 'Link')}
      </div>
    </BubbleMenu>
  );
}

const DRAG_NESTED_OPTIONS = { edgeDetection: 'left' };

export function NotionEditor({
  content,
  onUpdate,
  placeholder = "Type '/' for commands...",
  teamMembers = [],
  workspaceId = '',
  // D80 Tier 2 — used to fire in-app notifications when a user is
  // @-mentioned. The parent passes a stable callback that knows the
  // current note context (id, title, surface).
  onMention,
}) {
  // Slash-menu state. We mirror into refs so the editor's keydown handler
  // (set ONCE at editor creation) sees the live values, killing the
  // stale-closure bug that caused the slash menu to misbehave.
  const [slash, setSlash] = useState({ isOpen: false, position: { top: 0, left: 0 }, query: '', anchor: -1 });
  const slashRef = useRef(slash);
  useEffect(() => { slashRef.current = slash; }, [slash]);

  const [mentionMenu, setMentionMenu] = useState({ isOpen: false, position: { top: 0, left: 0 }, query: '', items: [], kind: 'person' });
  const [hoveredPos, setHoveredPos] = useState({ node: null, pos: null });
  const onNodeChangeStable = useCallback(({ node, pos }) => {
    setHoveredPos({ node, pos });
  }, []);
  const closeSlash = useCallback(() => setSlash({ isOpen: false, position: { top: 0, left: 0 }, query: '', anchor: -1 }), []);

  // D80 Tier 2 — hidden file inputs for Image/Video/File slash commands.
  const fileInputRef = useRef(null);
  const [uploadKind, setUploadKind] = useState('image'); // image|video|file
  const [uploadError, setUploadError] = useState('');
  const editorRef = useRef(null);

  const UPLOAD_LIMITS = {
    image: 25 * 1024 * 1024,
    video: 100 * 1024 * 1024,
    file:  25 * 1024 * 1024,
  };
  const ACCEPT_MAP = {
    image: 'image/*', video: 'video/*', file: '*/*',
  };

  const triggerUpload = (kind) => {
    setUploadKind(kind);
    setUploadError('');
    // Allow setUploadKind to flush before opening the picker.
    setTimeout(() => {
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
        fileInputRef.current.accept = ACCEPT_MAP[kind] || '*/*';
        fileInputRef.current.click();
      }
    }, 0);
  };

  const onFilePicked = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ed = editorRef.current;
    if (!ed) return;
    setUploadError('');
    try {
      const asset = await uploadNoteAsset(file, {
        workspaceId,
        maxBytes: UPLOAD_LIMITS[uploadKind],
      });
      if (uploadKind === 'image') {
        ed.chain().focus().clearNodes()
          .setImage({ src: asset.url, alt: asset.name, title: asset.name }).run();
      } else if (uploadKind === 'video') {
        ed.chain().focus().clearNodes()
          .insertVideo({ src: asset.url, name: asset.name,
                         mime_type: asset.mime_type, size: asset.size,
                         poster: asset.thumbnail_url || null }).run();
      } else {
        ed.chain().focus().clearNodes()
          .insertFile({ src: asset.url, name: asset.name,
                        mime_type: asset.mime_type, size: asset.size }).run();
      }
    } catch (err) {
      setUploadError(String(err.message || err));
      setTimeout(() => setUploadError(''), 4500);
    }
  };

  // ─── D80: Memoize the extensions array ───
  // Tiptap's React adapter compares extensions[i] BY REFERENCE
  // (compareOptions in @tiptap/react). If our extensions array
  // recreated `Mention.configure(...)` etc on every render, the adapter
  // would call `editor.setOptions({ extensions })` on every render,
  // which re-installs the Suggestion ProseMirror plugins. Once the
  // suggestion plugin's onStart fires and calls `setMentionMenu`, the
  // component re-renders → setOptions re-installs plugins → onStart
  // fires again → infinite loop → React bails with "Maximum update
  // depth exceeded". Memoising keeps the same references stable
  // across renders.
  //
  // Closures inside `items`/`render` legitimately capture `teamMembers`
  // and `workspaceId` at memo time. To keep mention suggestions live
  // when `teamMembers` arrives async, we mutate
  // `editor._d80EffectiveMembers` after the editor is created (see
  // below) and the items() callback prefers that.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const extensions = useMemo(() => [
    StarterKit.configure({
      heading: { levels: [1, 2, 3] },
      codeBlock: false,
    }),
    Placeholder.configure({ placeholder }),
    Underline,
    Link.configure({
      openOnClick: false,
      autolink: true,
      HTMLAttributes: { rel: 'noopener noreferrer', target: '_blank' },
    }),
    TaskList,
    TaskItem.configure({ nested: true }),
    Image.configure({ allowBase64: true }),
    Highlight,
    Typography,
    Callout,
    Table.configure({ resizable: true }),
    TableRow,
    TableCell,
    TableHeader,
    Details.configure({
      persist: true,
      openClassName: 'is-open',
      HTMLAttributes: { class: 'stellar-toggle' },
      renderToggleButton: ({ element, isOpen }) => {
        element.setAttribute('aria-label',
          isOpen ? 'Collapse toggle' : 'Expand toggle');
        element.setAttribute('data-open', isOpen ? 'true' : 'false');
        element.className = 'stellar-toggle-btn';
        element.innerHTML =
          '<svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">' +
          '<path d="M7 5l6 5-6 5V5z" fill="currentColor"></path>' +
          '</svg>';
      },
    }),
    DetailsSummary,
    DetailsContent,
    CodeBlock,
    VideoEmbed,
    FileAttachment,
    AiBlock.configure({ workspaceId }),
    PersonMention.configure({
      HTMLAttributes: { class: 'mention-tag', 'data-mention-type': 'person' },
      renderText({ node }) { return `@${node.attrs.label || node.attrs.id || ''}`; },
      renderHTML({ node, HTMLAttributes }) {
        return ['span', mergeAttributes(
          // Re-merge configure-time HTMLAttributes — tiptap 3.x doesn't
          // include `this.options.HTMLAttributes` automatically when you
          // override renderHTML.
          { class: 'mention-tag', 'data-mention-type': 'person' },
          HTMLAttributes,
          {
            'data-id': node.attrs.id || '', 'data-label': node.attrs.label || '',
            'data-kind': 'person',
          },
        ), `@${node.attrs.label || ''}`];
      },
      suggestion: {
        char: '@',
        items: ({ query, editor: ed }) => {
          const src = (ed?._d80EffectiveMembers && ed._d80EffectiveMembers.length)
            ? ed._d80EffectiveMembers
            : teamMembers;
          return (src || [])
            .filter(m => (m.name || m.email || '').toLowerCase().includes((query || '').toLowerCase()))
            .slice(0, 8)
            .map(m => ({
              id: m.id || m.user_id,
              label: m.name || m.email,
              email: m.email, role: m.role,
            }));
        },
        render: () => ({
          onStart: (props) => {
            const coords = props.clientRect?.();
            setMentionMenu({
              isOpen: true, kind: 'person',
              position: coords ? { top: coords.bottom + 8, left: coords.left } : { top: 0, left: 0 },
              items: props.items || [], command: props.command,
            });
          },
          onUpdate: (props) => {
            const coords = props.clientRect?.();
            setMentionMenu(prev => ({
              ...prev, isOpen: true, kind: 'person',
              items: props.items || [], command: props.command,
              ...(coords ? { position: { top: coords.bottom + 8, left: coords.left } } : {}),
            }));
          },
          onExit: () => setMentionMenu(prev => ({ ...prev, isOpen: false })),
          onKeyDown: (props) => {
            if (props.event.key === 'Escape') {
              setMentionMenu(prev => ({ ...prev, isOpen: false }));
              return true;
            }
            return false;
          },
        }),
      },
    }),
    PageMention.configure({
      HTMLAttributes: { class: 'mention-tag mention-page', 'data-mention-type': 'page' },
      renderText({ node }) { return `+${node.attrs.label || node.attrs.id || ''}`; },
      renderHTML({ node, HTMLAttributes }) {
        return ['span', mergeAttributes(
          { class: 'mention-tag mention-page', 'data-mention-type': 'page' },
          HTMLAttributes,
          {
            'data-id': node.attrs.id || '', 'data-label': node.attrs.label || '',
            'data-kind': node.attrs.kind || 'note',
          },
        ), `+${node.attrs.label || ''}`];
      },
      suggestion: {
        char: '+',
        items: async ({ query }) => {
          if (!workspaceId) return [];
          try {
            const token = localStorage.getItem('stellar_token');
            const r = await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/notes-search/${workspaceId}?q=${encodeURIComponent(query || '')}`,
              { headers: token ? { Authorization: `Bearer ${token}` } : {} },
            );
            if (!r.ok) return [];
            const data = await r.json();
            return (data || []).map(n => ({
              id: n.id, label: n.title, kind: n.kind === 'client_page' ? 'client_page' : 'note',
            }));
          } catch { return []; }
        },
        render: () => ({
          onStart: (props) => {
            const coords = props.clientRect?.();
            setMentionMenu({
              isOpen: true, kind: 'page',
              position: coords ? { top: coords.bottom + 8, left: coords.left } : { top: 0, left: 0 },
              items: props.items || [], command: props.command,
            });
          },
          onUpdate: (props) => {
            const coords = props.clientRect?.();
            setMentionMenu(prev => ({
              ...prev, isOpen: true, kind: 'page',
              items: props.items || [], command: props.command,
              ...(coords ? { position: { top: coords.bottom + 8, left: coords.left } } : {}),
            }));
          },
          onExit: () => setMentionMenu(prev => ({ ...prev, isOpen: false })),
          onKeyDown: (props) => {
            if (props.event.key === 'Escape') {
              setMentionMenu(prev => ({ ...prev, isOpen: false }));
              return true;
            }
            return false;
          },
        }),
      },
    }),
  // Stable deps: workspaceId + placeholder. teamMembers is intentionally
  // omitted — we hot-swap it via editor._d80EffectiveMembers below.
  ], [workspaceId, placeholder]);



  // Stable callbacks captured via refs to avoid re-creating the editor.
  // onUpdate / onMention props change every parent render — using refs
  // means the editor instance can remain stable.
  const onUpdatePropRef = useRef(onUpdate);
  const onMentionPropRef = useRef(onMention);
  useEffect(() => { onUpdatePropRef.current = onUpdate; }, [onUpdate]);
  useEffect(() => { onMentionPropRef.current = onMention; }, [onMention]);

  // Stable initial content + editorProps (memoised so Tiptap's
  // compareOptions on every render doesn't trigger a setOptions
  // re-install of the suggestion plugins, which causes onStart to
  // re-fire → setMentionMenu → re-render → infinite loop).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialContent = useMemo(
    () => content || { type: 'doc', content: [{ type: 'paragraph' }] },
    // We accept the initial `content` once. Subsequent changes are
    // driven by `editor.commands.setContent` via the parent if needed.
    [],
  );

  const editorProps = useMemo(() => ({
    attributes: {
      class: 'notion-editor focus:outline-none min-h-[300px]',
    },
    handleKeyDown: (view, event) => {
      if (event.key === '/' && !slashRef.current.isOpen && view.state.selection.empty) {
        const anchor = view.state.selection.from;
        setTimeout(() => {
          try {
            const coords = view.coordsAtPos(anchor);
            setSlash({
              isOpen: true,
              position: { top: coords.bottom + 8, left: coords.left },
              query: '',
              anchor,
            });
          } catch {}
        }, 0);
      }
      return false;
    },
  }), []);

  const onEditorUpdate = useCallback(({ editor }) => {
    if (slashRef.current.isOpen) {
      const { from } = editor.state.selection;
      const anchor = slashRef.current.anchor;
      if (anchor < 0 || from <= anchor) {
        closeSlash();
      } else {
        const text = editor.state.doc.textBetween(anchor, from, '\n', '\0');
        if (!text.startsWith('/') || text.includes('\n')) {
          closeSlash();
        } else {
          const q = text.slice(1);
          setSlash(s => ({ ...s, query: q }));
        }
      }
    }
    try {
      const handler = onMentionPropRef.current;
      if (handler) {
        const seen = (editorRef.current?._d80MentionedIds || new Set());
        const doc = editor.state.doc;
        doc.descendants((node) => {
          if (node.type?.name === 'personMention') {
            const id = node.attrs?.id;
            const label = node.attrs?.label;
            if (id && !seen.has(id)) {
              seen.add(id);
              Promise.resolve(handler({ id, label })).catch(() => {});
            }
          }
        });
        if (editorRef.current) editorRef.current._d80MentionedIds = seen;
      }
    } catch {}
    if (onUpdatePropRef.current) onUpdatePropRef.current(editor.getJSON());
  }, [closeSlash]);

  const editor = useEditor({
    extensions: extensions,
    content: initialContent,
    editorProps: editorProps,
    onUpdate: onEditorUpdate,
  });

  // Keep a stable ref so callbacks (uploadHelper, mention notify) can
  // reach the editor instance without re-binding the closure.
  editorRef.current = editor;

  // D80 Tier 2 — auto-fetch workspace members so @-mentions work even
  // when the parent didn't pre-load them. We only call this when
  // workspaceId is set and the parent hasn't supplied members.
  const [autoMembers, setAutoMembers] = useState([]);
  useEffect(() => {
    if (!workspaceId || teamMembers.length > 0) { setAutoMembers([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('stellar_token');
        const r = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/workspaces/${workspaceId}/members`,
          { headers: token ? { Authorization: `Bearer ${token}` } : {} },
        );
        if (!r.ok || cancelled) return;
        const data = await r.json();
        if (!cancelled) {
          // Normalize the /workspaces/{wid}/members response (where
          // user info lives under `m.user`) into the flat shape the
          // mention suggestion expects.
          const flat = (Array.isArray(data) ? data : []).map(m => ({
            id: m.user?.id || m.user_id || m.id,
            user_id: m.user?.id || m.user_id,
            name: m.user?.name || m.name || m.user?.email || m.email,
            email: m.user?.email || m.email,
            role: m.user?.role || m.role,
          }));
          setAutoMembers(flat);
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [workspaceId, teamMembers.length]);
  // Mutating the editor's stored teamMembers list in place is the
  // cheapest hack to make the Mention suggestion config see fresh data
  // without re-creating the editor instance.
  const effectiveMembers = teamMembers.length ? teamMembers : autoMembers;
  if (editor) editor._d80EffectiveMembers = effectiveMembers;

  // Close slash menu on outside click (mouseDown elsewhere).
  useEffect(() => {
    if (!slash.isOpen) return;
    const h = (e) => {
      if (e.target.closest('[data-testid="slash-command-menu"]')) return;
      closeSlash();
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [slash.isOpen, closeSlash]);

  if (!editor) return null;

  // Insert a new empty paragraph AFTER the currently hovered block and
  // immediately open the slash menu so the user can pick a block type.
  const handleAddClick = () => {
    if (!hoveredPos || hoveredPos.pos == null) return;
    const node = hoveredPos.node;
    if (!node) return;
    const endOfBlock = hoveredPos.pos + node.nodeSize;
    editor
      .chain()
      .focus()
      .insertContentAt(endOfBlock, { type: 'paragraph' })
      .setTextSelection(endOfBlock + 1)
      .run();
    // Open slash menu at the new position.
    setTimeout(() => {
      try {
        const view = editor.view;
        const { from } = view.state.selection;
        const coords = view.coordsAtPos(from);
        setSlash({
          isOpen: true,
          position: { top: coords.bottom + 8, left: coords.left },
          query: '',
          anchor: from,
        });
      } catch {}
    }, 10);
  };

  return (
    <div className="relative" data-testid="notion-editor">
      <InlineToolbar editor={editor} />
      {/* D80 Tier 1 — drag handle + add handle (Notion-style 6-dot grip).
          The handle floats next to the currently hovered block; clicking
          and dragging reorders blocks; `nested` lets us nest list items,
          blockquote/callout contents, and (most importantly) drag into /
          out of Toggle (`detailsContent`) children. */}
      <DragHandle
        editor={editor}
        nested={DRAG_NESTED_OPTIONS}
        onNodeChange={onNodeChangeStable}
      >
        <div className="flex items-center gap-0.5 -ml-1 select-none" data-testid="block-handle-group">
          <button
            type="button"
            aria-label="Insert block below"
            data-testid="block-add-handle"
            className="h-5 w-5 rounded hover:bg-foreground/[0.08] flex items-center justify-center text-muted-foreground/70 hover:text-foreground transition-colors"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleAddClick}
            title="Click to add a block below"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            aria-label="Drag to reorder block"
            data-testid="block-drag-handle"
            className="h-5 w-5 rounded hover:bg-foreground/[0.08] flex items-center justify-center text-muted-foreground/70 hover:text-foreground transition-colors cursor-grab active:cursor-grabbing"
            title="Drag to reorder · click for actions"
          >
            <GripVertical className="h-3.5 w-3.5" />
          </button>
        </div>
      </DragHandle>
      <EditorContent editor={editor} />
      <TableToolbar editor={editor} />
      {/* D80 Tier 2 — hidden uploader (used by Image/Video/File slash). */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={onFilePicked}
        data-testid="notion-file-input"
      />
      {uploadError && (
        <div
          className="fixed bottom-6 right-6 z-[60] bg-destructive/90 text-destructive-foreground text-xs px-3 py-2 rounded-md shadow-xl"
          data-testid="upload-error"
        >{uploadError}</div>
      )}
      <SlashMenu
        editor={editor}
        isOpen={slash.isOpen}
        position={slash.position}
        query={slash.query}
        slashAnchor={slash.anchor}
        onClose={closeSlash}
        slashContext={{ workspaceId, triggerUpload }}
      />
      {mentionMenu.isOpen && (
        <div
          className="fixed z-50 bg-popover border border-border rounded-lg shadow-lg p-1 w-[240px] max-h-[260px] overflow-y-auto"
          style={{ top: mentionMenu.position.top, left: mentionMenu.position.left }}
          data-testid={`mention-menu-${mentionMenu.kind}`}
        >
          <p className="text-[10px] font-semibold text-muted-foreground/50 uppercase tracking-wider px-3 py-1.5">
            {mentionMenu.kind === 'page' ? 'Link a note' : 'Mention person'}
          </p>
          {mentionMenu.items.length === 0 ? (
            <p className="text-[11px] text-muted-foreground px-3 py-2.5 italic">
              {mentionMenu.kind === 'page' ? 'No matching notes' : 'No matching teammates'}
            </p>
          ) : mentionMenu.items.map((item, i) => (
            <button
              key={item.id || i}
              type="button"
              className="flex items-center gap-2.5 w-full px-3 py-2 rounded-md text-left hover:bg-foreground/[0.04] transition-colors"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                if (mentionMenu.command) {
                  mentionMenu.command({
                    id: item.id, label: item.label || item.name || item.email,
                  });
                }
              }}
              data-testid={`mention-item-${item.label || item.name || item.email}`}
            >
              <div className={`h-6 w-6 rounded-full flex items-center justify-center shrink-0 ${mentionMenu.kind === 'page' ? 'bg-gold/15 text-gold' : 'bg-secondary'}`}>
                <span className="text-[9px] font-medium">
                  {((item.label || item.name || '?')[0] || '?').toUpperCase()}
                </span>
              </div>
              <div className="min-w-0">
                <p className="text-[12px] font-medium truncate">{item.label || item.name || item.email}</p>
                {item.email && mentionMenu.kind !== 'page' && (
                  <p className="text-[10px] text-muted-foreground truncate">{item.email}</p>
                )}
                {item.role && <p className="text-[10px] text-muted-foreground capitalize">{item.role}</p>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
